/**
 * Admin — create poll: options builder (add/remove, min 2), voting scope
 * (all wings / wing A–D), deadline, anonymity toggle.
 * Wired: POST /api/admin/polls. Port of screens-admin.jsx CreatePoll.
 */
import React from 'react';
import { View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { InfoNote, Toggle } from '@/components/gate/bits';
import { AdminScreenShell, goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar, Segmented } from '@/features/community/bits';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Field, IconButton, Input, Screen } from '@/ui';

const SCOPES = [
  { key: 'ALL', label: 'All wings' },
  { key: 'A', label: 'Wing A' },
  { key: 'B', label: 'Wing B' },
  { key: 'C', label: 'Wing C' },
  { key: 'D', label: 'Wing D' },
];

export default function CreatePollScreen() {
  const { colors } = useTheme();
  const [question, setQuestion] = React.useState('');
  const [opts, setOpts] = React.useState<string[]>(['', '']);
  const [scope, setScope] = React.useState('ALL');
  const [deadline, setDeadline] = React.useState('');
  const [anon, setAnon] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const filled = opts.map((o) => o.trim()).filter(Boolean);
  const canPublish = question.trim().length > 0 && filled.length >= 2;

  const publish = async () => {
    if (!canPublish) return;
    setSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/admin/polls', {
        question: question.trim(),
        options: filled,
        scope,
        deadline: deadline.trim() || undefined,
        anonymous: anon,
      });
      goBack();
    } catch {
      setNotice('Could not publish the poll right now. Try again in a bit.');
      setSaving(false);
    }
  };

  return (
    <AdminScreenShell
      title="Create poll"
      right={
        <IconButton label="Discard" onPress={goBack}>
          <Icon name="x" size={20} color={colors.textPrimary} />
        </IconButton>
      }
    >
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <Field label="Question">
          <Input
            value={question}
            onChangeText={setQuestion}
            placeholder="What do you want to ask residents?"
            multiline
            style={{ minHeight: 64, textAlignVertical: 'top' }}
          />
        </Field>
        <Field label="Options">
          <View style={{ gap: space.s2 }}>
            {opts.map((o, i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                <Input
                  value={o}
                  onChangeText={(t) => setOpts((s) => s.map((x, j) => (j === i ? t : x)))}
                  placeholder={`Option ${i + 1}`}
                  style={{ flex: 1 }}
                />
                {opts.length > 2 ? (
                  <IconButton label={`Remove option ${i + 1}`} onPress={() => setOpts((s) => s.filter((_, j) => j !== i))}>
                    <Icon name="x" size={18} color={colors.textSecondary} />
                  </IconButton>
                ) : null}
              </View>
            ))}
            <View style={{ alignSelf: 'flex-start' }}>
              <SmallButton label="Add option" icon="plus" onPress={() => setOpts((s) => [...s, ''])} />
            </View>
          </View>
        </Field>
        <Field label="Who can vote">
          <Segmented options={SCOPES} value={scope} onChange={setScope} />
        </Field>
        <Field label="Deadline">
          <Input value={deadline} onChangeText={setDeadline} placeholder="e.g. 30 Oct 2026" />
        </Field>
        <View style={{
          backgroundColor: colors.canvas1, borderRadius: radius.lg, padding: space.s4,
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.s3,
        }}>
          <View style={{ flex: 1 }}>
            <AppText variant="bodyMd" weight="500">Anonymous voting</AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>Hide who voted for what</AppText>
          </View>
          <Toggle on={anon} onChange={setAnon} label="Anonymous voting" />
        </View>
        {notice ? <InfoNote icon="info" tint={colors.danger500}>{notice}</InfoNote> : null}
      </Screen>
      <BottomBar>
        <Button title="Publish poll" size="lg" disabled={!canPublish} loading={saving} onPress={() => { void publish(); }} />
      </BottomBar>
    </AdminScreenShell>
  );
}
